import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import Select from "react-select";
import DatePicker from "react-datepicker";
import Moment from "moment";
import { get, post } from "aws-amplify/api";
import SLMSearchPageShell from "../SLMSearchPageShell";
import SLMSaleList from "./SLMSaleList";
import SLMOwnerIsCompanyMapping from "./SLMOwnerIsCompanyMapping";
import { createElementIdFactory } from "utils/elementIds";
import "react-datepicker/dist/react-datepicker.css";
import "./SLMSaleSearch.css";

const getLookupGroup = (lookups, names) => {
  const list = Array.isArray(lookups) ? lookups : [];
  return list.find((item) => names.includes(item?.look_up)) || null;
};

const mapLookupOptions = (group, valueKeys, labelKeys) => {
  const items = Array.isArray(group?.list) ? group.list : [];

  return items
    .map((item) => {
      if (typeof item === "string") {
        return { value: item, label: item };
      }

      const value = valueKeys.map((key) => item?.[key]).find((val) => val !== undefined && val !== null && String(val) !== "");
      const label = labelKeys.map((key) => item?.[key]).find((val) => val !== undefined && val !== null);

      if (value === undefined || value === null || String(value) === "") {
        return null;
      }

      return {
        ...item,
        value,
        label: label ?? String(value),
      };
    })
    .filter(Boolean);
};

function SLMSaleSearch({ isAuthenticated, user }) {
  const [isLoading, setIsLoading] = useState(true);
  const [sales, setSales] = useState([]);
  const [filter, setFilter] = useState({});
  const [searchCount, setSearchCount] = useState(0);

  const [stateOptions, setStateOptions] = useState([]);
  const [broadSpaceUseOptions, setBroadSpaceUseOptions] = useState([]);
  const [spaceUseOptions, setSpaceUseOptions] = useState([]);
  const [titleBasisOptions, setTitleBasisOptions] = useState([]);
  const [saleMethodOptions, setSaleMethodOptions] = useState([]);

  const [showIndividualBuyerSeller, setShowIndividualBuyerSeller] = useState(false);
  const [ownerIsCompanyMapping, setOwnerIsCompanyMapping] = useState([]);

  const [selectedState, setSelectedState] = useState(null);
  const [selectedBroadSpaceUseCategories, setSelectedBroadSpaceUseCategories] = useState(null);
  const [selectedSpaceUseCategories, setSelectedSpaceUseCategories] = useState(null);
  const [selectedTitleBasisCategories, setSelectedTitleBasisCategories] = useState(null);
  const [selectedSaleMethodCategories, setSelectedSaleMethodCategories] = useState(null);

  const [createdby, setCreatedby] = useState("");
  const [editedby, setEditedby] = useState("");
  const [printdatefrom, setPrintdatefrom] = useState(null);
  const [printdateto, setPrintdateto] = useState(null);
  const [statdatefrom, setStatdatefrom] = useState(null);
  const [statdateto, setStatdateto] = useState(null);
  const [pricefrom, setPricefrom] = useState("");
  const [priceto, setPriceto] = useState("");
  const [pricepsqmbuildingfrom, setPricepsqmbuildingfrom] = useState("");
  const [pricepsqmbuildingto, setPricepsqmbuildingto] = useState("");
  const [siteareafrom, setSiteareafrom] = useState("");
  const [siteareato, setSiteareato] = useState("");
  const [pricepsqmlandfrom, setPricepsqmlandfrom] = useState("");
  const [pricepsqmlandto, setPricepsqmlandto] = useState("");
  const [yieldamtfrom, setYieldamtfrom] = useState("");
  const [yieldamtto, setYieldamtto] = useState("");
  const [broadspace, setBroadspace] = useState("");
  const [broadspaceValue, setBroadspaceValue] = useState("");
  const [spaceuse, setSpaceuse] = useState("");
  const [titlebasis, setTitlebasis] = useState("");
  const [salesmethod, setSalesmethod] = useState("");
  const [buildingname, setBuildingname] = useState("");
  const [streetname, setStreetname] = useState("");
  const [streetext, setStreetext] = useState("");
  const [suburb, setSuburb] = useState("");
  const [postcode, setPostcode] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [agent, setAgent] = useState("");
  const [agency, setAgency] = useState("");
  const [buyer, setBuyer] = useState("");
  const [vendor, setVendor] = useState("");
  const [saletype, setSaletype] = useState("");
  const [transferno, setTransferno] = useState("");
  const [transfernonull, setTransfernonull] = useState(false);
  const [transfernoduplicate, setTransfernoduplicate] = useState(false);
  const [pricenull, setPricenull] = useState(false);

  const createId = useMemo(() => createElementIdFactory("slm-sale-search"), []);

  const elementIds = useMemo(
    () => ({
      searchButton: createId("action", "search", "button"),
      clearButton: createId("action", "clear", "button"),
      exportPimsButton: createId("action", "export", "pims", "button"),
      exportRcaMsciButton: createId("action", "export", "rca-msci", "button"),
      reviewBuyerSellerButton: createId("action", "review", "buyer-seller", "button"),
      saleTypeSold: createId("field", "sale", "type", "sold", "radio"),
      saleTypeForSale: createId("field", "sale", "type", "for-sale", "radio"),
      saleTypeBoth: createId("field", "sale", "type", "both", "radio"),
      createdBy: createId("field", "created-by", "input"),
      editedBy: createId("field", "edited-by", "input"),
      printDateFrom: createId("field", "print-date", "from", "input"),
      printDateTo: createId("field", "print-date", "to", "input"),
      statDateFrom: createId("field", "stat-date", "from", "input"),
      statDateTo: createId("field", "stat-date", "to", "input"),
      priceFrom: createId("field", "price", "from", "input"),
      priceTo: createId("field", "price", "to", "input"),
      priceNull: createId("field", "price", "null", "checkbox"),
      transferNo: createId("field", "transfer-no", "input"),
      transferNoNull: createId("field", "transfer-no", "null", "checkbox"),
      transferNoDuplicate: createId("field", "transfer-no", "duplicate", "checkbox"),
      pricePerSqmBuildingFrom: createId("field", "price-per-sqm-building", "from", "input"),
      pricePerSqmBuildingTo: createId("field", "price-per-sqm-building", "to", "input"),
      siteAreaFrom: createId("field", "site-area", "from", "input"),
      siteAreaTo: createId("field", "site-area", "to", "input"),
      pricePerSqmLandFrom: createId("field", "price-per-sqm-land", "from", "input"),
      pricePerSqmLandTo: createId("field", "price-per-sqm-land", "to", "input"),
      yieldFrom: createId("field", "yield", "from", "input"),
      yieldTo: createId("field", "yield", "to", "input"),
      broadSpaceUse: createId("field", "broad-space-use", "select"),
      spaceUse: createId("field", "space-use", "select"),
      titleBasis: createId("field", "title-basis", "select"),
      saleMethod: createId("field", "sale-method", "select"),
      buildingName: createId("field", "building-name", "input"),
      streetName: createId("field", "street-name", "input"),
      streetExt: createId("field", "street-ext", "input"),
      suburb: createId("field", "suburb", "input"),
      state: createId("field", "state", "select"),
      postcode: createId("field", "postcode", "input"),
      agent: createId("field", "agent", "input"),
      agency: createId("field", "agency", "input"),
      buyer: createId("field", "buyer", "input"),
      vendor: createId("field", "vendor", "input"),
      mappingModal: createId("modal", "owner-company-mapping"),
    }),
    [createId]
  );

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    document.title = "SLM Sales Search";

    (async () => {
      try {
        const [statesResponse, slmLookupsResponse] = await Promise.all([
          get({ apiName: "colin", path: "/slmstates" }).response,
          get({ apiName: "colin", path: "/slmlookups" }).response,
        ]);

        const [states, slmLookups] = await Promise.all([statesResponse.body.json(), slmLookupsResponse.body.json()]);

        const broadSpaceUseGroup = getLookupGroup(slmLookups, ["broad_space_use"]);
        const spaceUseGroup = getLookupGroup(slmLookups, ["space_use"]);
        const titleBasisGroup = getLookupGroup(slmLookups, ["sales_title_category"]);
        const saleMethodGroup = getLookupGroup(slmLookups, ["sales_method_category"]);

        setStateOptions(Array.isArray(states) ? states : []);
        setBroadSpaceUseOptions(
          mapLookupOptions(
            broadSpaceUseGroup,
            ["value", "broad_space_use_id", "id", "code", "label", "name"],
            ["label", "broad_space_use_code", "broad_space_use", "name", "code", "value"]
          )
        );
        setSpaceUseOptions(
          mapLookupOptions(
            spaceUseGroup,
            ["value", "space_use_id", "id", "code", "label", "name"],
            ["label", "space_use", "space_use_name", "name", "code", "value"]
          )
        );
        setTitleBasisOptions(
          mapLookupOptions(
            titleBasisGroup,
            ["value", "title_basis_cat_id", "title_basis_id", "id", "code", "label", "name"],
            ["label", "title_basis", "title_basis_code", "name", "code", "value"]
          )
        );
        setSaleMethodOptions(
          mapLookupOptions(
            saleMethodGroup,
            ["value", "sale_method_cat_id", "sale_method_id", "id", "code", "label", "name"],
            ["label", "sale_method", "sale_method_code", "name", "code", "value"]
          )
        );
      } catch (error) {
        alert(error + ". Please refresh the page (F5) to try again.");
      } finally {
        setIsLoading(false);
      }
    })();
  }, [isAuthenticated]);

  const numericValue = (value) => String(value ?? "").replace(/[^0-9.]/g, "");

  const hasValidDate = (value) => value instanceof Date && !Number.isNaN(value.getTime());

  const isFetchTimeoutError = (error) => {
    const statusCode =
      error?.statusCode ||
      error?.response?.status ||
      error?.$metadata?.httpStatusCode ||
      error?.cause?.statusCode;

    if (statusCode === 504) {
      return true;
    }

    const details = [
      error?.name,
      error?.message,
      error?.code,
      error?.cause?.message,
      typeof error === "string" ? error : "",
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return (
      details.includes("timeout") ||
      details.includes("timed out") ||
      details.includes("504") ||
      details.includes("networkerror") ||
      details.includes("network error")
    );
  };

  const buildFilter = () => ({
    createdby,
    editedby,
    printdatefrom: printdatefrom ? Moment(printdatefrom).local().format() : "",
    printdateto: printdateto ? Moment(printdateto).local().format() : "",
    statdatefrom: statdatefrom ? Moment(statdatefrom).local().format() : "",
    statdateto: statdateto ? Moment(statdateto).local().format() : "",
    pricefrom,
    priceto,
    pricepsqmbuildingfrom,
    pricepsqmbuildingto,
    siteareafrom,
    siteareato,
    pricepsqmlandfrom,
    pricepsqmlandto,
    yieldamtfrom,
    yieldamtto,
    broadspace,
    spaceuse,
    titlebasis,
    salesmethod,
    buildingname,
    streetname,
    streetext,
    suburb,
    postcode,
    state: stateCode,
    agent,
    agency,
    buyer,
    vendor,
    saletype,
    transferno,
    transfernonull,
    transfernoduplicate,
    pricenull,
  });

  const getSales = async (activeFilter) => {
    const response = await get({
      apiName: "colin",
      path: "/slmsales",
      options: { queryParams: activeFilter },
    }).response;

    return response.body.json();
  };

  const getSalesExport = async (activeFilter) => {
    const response = await get({
      apiName: "colin",
      path: "/slmsalesexport",
      options: { queryParams: activeFilter },
    }).response;

    return response.body.json();
  };

  const getOwnerIsCompanyNameCheck = async (activeFilter) => {
    const response = await get({
      apiName: "colin",
      path: "/slmiscompanynamescreening",
      options: { queryParams: activeFilter },
    }).response;

    return response.body.json();
  };

  const formatExportDate = (date) => {
    if (!date) {
      return "";
    }

    return Moment(date).format("YYYY-MM-DD");
  };

  const buildRcaExportParams = () => ({
    p_printdatefrom: formatExportDate(printdatefrom),
    p_printdateto: formatExportDate(printdateto),
    p_saletype: saletype || "SOLD",
  });

  const generateRcaExport = async () => {
    const response = await post({
      apiName: "colin",
      path: "/slmrca",
      options: {
        body: {
          ...buildRcaExportParams(),
        },
      },
    }).response;

    const statusCode = Number(response?.statusCode ?? response?.status ?? response?.$metadata?.httpStatusCode ?? 200);
    if (statusCode >= 400) {
      throw new Error(`SLM RCA export request failed with status ${statusCode}`);
    }

    return response.body.json();
  };

  const downloadTextFile = (content, fileName, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const fallbackRcaFileName = (totalRowCount = 0) => {
    const safeTotalRowCount = Number.isFinite(Number(totalRowCount)) ? Number(totalRowCount) : 0;
    return `RCA_${safeTotalRowCount}_ALL_SALES`;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    const activeFilter = buildFilter();

    try {
      const nextSales = await getSales(activeFilter);
      setSales(Array.isArray(nextSales) ? nextSales : []);
      setFilter(activeFilter);
      setSearchCount((count) => count + 1);
    } catch (error) {
      const fetchTimeoutErrorMessage = "Fetching sales data is taking a while. Kindly refine your search filters and try again.";
      if (isFetchTimeoutError(error)) {
        alert(fetchTimeoutErrorMessage);
      } else {
        alert(fetchTimeoutErrorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setCreatedby("");
    setEditedby("");
    setPrintdatefrom(null);
    setPrintdateto(null);
    setStatdatefrom(null);
    setStatdateto(null);
    setPricefrom("");
    setPriceto("");
    setPricepsqmbuildingfrom("");
    setPricepsqmbuildingto("");
    setSiteareafrom("");
    setSiteareato("");
    setPricepsqmlandfrom("");
    setPricepsqmlandto("");
    setYieldamtfrom("");
    setYieldamtto("");
    setBroadspace("");
    setBroadspaceValue("");
    setSpaceuse("");
    setTitlebasis("");
    setSalesmethod("");
    setBuildingname("");
    setStreetname("");
    setStreetext("");
    setSuburb("");
    setPostcode("");
    setStateCode("");
    setAgent("");
    setAgency("");
    setBuyer("");
    setVendor("");
    setSaletype("");
    setTransferno("");
    setTransfernonull(false);
    setTransfernoduplicate(false);
    setPricenull(false);
    setSelectedState(null);
    setSelectedBroadSpaceUseCategories(null);
    setSelectedSpaceUseCategories(null);
    setSelectedTitleBasisCategories(null);
    setSelectedSaleMethodCategories(null);
  };

  const filteredSpaceUseOptions = useMemo(
    () =>
      (spaceUseOptions || []).filter((option) => {
        if (!broadspaceValue) {
          return true;
        }

        return String(option?.supplementary_details?.broad_space_use_id ?? "") === String(broadspaceValue);
      }),
    [spaceUseOptions, broadspaceValue]
  );

  const resolveExportRows = (payload) => {
    if (Array.isArray(payload)) {
      if (Array.isArray(payload[0]?.results)) {
        return payload[0].results;
      }
      return payload;
    }

    if (Array.isArray(payload?.results)) {
      return payload.results;
    }

    return [];
  };

  const exportPimsRecords = async () => {
    if (!hasValidDate(printdatefrom) || !hasValidDate(printdateto)) {
      alert("You must enter print date from and to before running an export");
      return;
    }

    if (!stateCode) {
      alert("You must select a state before running an export");
      return;
    }

    setIsLoading(true);

    try {
      const payload = await getSalesExport(buildFilter());
      const rows = resolveExportRows(payload);

      if (!rows.length) {
        alert("No records found for export.");
        return;
      }

      const pimContent = rows
        .map((record) =>
          Object.values(record)
            .map((value) => {
              if (value === null || value === undefined) {
                return "";
              }
              if (typeof value === "number") {
                return value;
              }
              return `"${String(value).replace(/\r/g, "").replace(/\n/g, "").replace(/"/g, '""')}"`;
            })
            .join(",")
        )
        .join("\r\n");

      const today = new Date(printdateto);
      const dd = String(today.getDate()).padStart(2, "0");
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const yy = String(today.getFullYear()).slice(2, 4);

      const stateSuffixMap = {
        ACT: "a",
        NT: "d",
        NSW: "n",
        QLD: "q",
        SA: "s",
        TAS: "t",
        VIC: "v",
        WA: "w",
      };

      const stateSuffix = stateSuffixMap[stateCode] || "";
      const fileName = `s${yy}${mm}${dd}${stateSuffix}.pim`;

      const blob = new Blob([pimContent], { type: "text/plain;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const exportErrorMessage = "The PIM export is taking longer than expected due to a large amount of data requested. Kindly refine your search filters and try again.";
      if (isFetchTimeoutError(error)) {
        alert(exportErrorMessage);
      } else {
        alert(exportErrorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const exportRcaMsciRecords = async () => {
    if (!printdatefrom || !printdateto) {
      alert("You must enter print date from and to before running an RCA/MSCI export");
      return;
    }

    setIsLoading(true);

    try {
      const payload = await generateRcaExport();

      if (payload?.result === false) {
        throw new Error(payload?.error_message || "Failed to generate RCA/MSCI export.");
      }

      const totalRowCount =
        payload?.total_row_count ??
        payload?.totalRowCount ??
        payload?.rowcount ??
        payload?.row_count ??
        payload?.count ??
        0;
      const fileName = payload?.filename || fallbackRcaFileName(totalRowCount);
      const csvContent = payload?.csv_content;

      if (typeof csvContent === "string" && csvContent.length > 0) {
        downloadTextFile(csvContent, fileName, "text/csv;charset=utf-8;");
        return;
      }

      throw new Error("The RCA/MSCI export completed but no downloadable file was returned by /slmrca.");
    } catch (error) {
      const exportErrorMessage = "The RCA/MSCI export is taking longer than expected due to a large amount of data requested. Kindly refine your search filters and try again.";
      if (isFetchTimeoutError(error)) {
        alert(exportErrorMessage);
      } else {
        alert(error?.message || exportErrorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getIsCompanyNameCheck = async () => {
    if (!printdatefrom || !printdateto) {
      alert("You must enter print date from and to");
      return;
    }

    setIsLoading(true);

    try {
      const mapping = await getOwnerIsCompanyNameCheck(buildFilter());
      setOwnerIsCompanyMapping(Array.isArray(mapping) ? mapping : []);
      setShowIndividualBuyerSeller(true);
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const updateOwnerIsCompanyMapping = (nextData) => {
    setOwnerIsCompanyMapping(Array.isArray(nextData) ? nextData : []);
  };

  const renderAlignedCheckbox = (id, label, checked, onChange) => (
    <div className="slm-sale-search-checkbox-focus">
      <input
        id={id}
        type="checkbox"
        className="slm-sale-search-checkbox-input"
        checked={Boolean(checked)}
        onChange={onChange}
      />
      <label htmlFor={id} className="slm-sale-search-checkbox-label">
        {label}
      </label>
    </div>
  );

  const topActions = (
    <>
      <Row className="mb-3 g-2">
        <Col xs="auto">
          <Button id={elementIds.searchButton} type="submit" size="sm">
            Search
          </Button>
        </Col>
        <Col xs="auto">
          <Button id={elementIds.clearButton} type="button" size="sm" onClick={clearFilters}>
            Clear Filters
          </Button>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col xs={12}>
          <div className="slm-lease-radio-group">
            <div className="slm-lease-radio-option">
              <input
                id={elementIds.saleTypeSold}
                name="sale_type"
                type="radio"
                className="slm-lease-radio-input"
                checked={saletype === "SOLD"}
                onChange={() => setSaletype("SOLD")}
              />
              <label htmlFor={elementIds.saleTypeSold} className="slm-lease-radio-label">SOLD</label>
            </div>

            <div className="slm-lease-radio-option">
              <input
                id={elementIds.saleTypeForSale}
                name="sale_type"
                type="radio"
                className="slm-lease-radio-input"
                checked={saletype === "For Sale"}
                onChange={() => setSaletype("For Sale")}
              />
              <label htmlFor={elementIds.saleTypeForSale} className="slm-lease-radio-label">For Sale</label>
            </div>

            <div className="slm-lease-radio-option">
              <input
                id={elementIds.saleTypeBoth}
                name="sale_type"
                type="radio"
                className="slm-lease-radio-input"
                checked={saletype === "Both"}
                onChange={() => setSaletype("Both")}
              />
              <label htmlFor={elementIds.saleTypeBoth} className="slm-lease-radio-label">Both</label>
            </div>
          </div>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col xs={12} sm={6}>
          <Form.Group controlId={elementIds.createdBy}>
            <Form.Control id={elementIds.createdBy} value={createdby} onChange={(event) => setCreatedby(event.target.value)} placeholder="Created By starting" />
          </Form.Group>
        </Col>
        <Col xs={12} sm={6}>
          <Form.Group controlId={elementIds.editedBy}>
            <Form.Control id={elementIds.editedBy} value={editedby} onChange={(event) => setEditedby(event.target.value)} placeholder="Last Edited By starting" />
          </Form.Group>
        </Col>
      </Row>
    </>
  );

  const filters = (
    <>
      <Row className="mb-2 g-2">
        <Col xs={12} lg={4} className="d-flex align-items-center">Print Date</Col>
        <Col xs={12} sm={6} lg={4}>
          <DatePicker
            id={elementIds.printDateFrom}
            selected={printdatefrom}
            onChange={(date) => setPrintdatefrom(date || null)}
            dateFormat="dd/MM/yyyy"
            className="form-control"
            isClearable
            placeholderText="From"
          />
        </Col>
        <Col xs={12} sm={6} lg={4}>
          <DatePicker
            id={elementIds.printDateTo}
            selected={printdateto}
            onChange={(date) => setPrintdateto(date || null)}
            dateFormat="dd/MM/yyyy"
            className="form-control"
            isClearable
            placeholderText="To"
          />
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col xs={12} lg={4} className="d-flex align-items-center">Stat Date</Col>
        <Col xs={12} sm={6} lg={4}>
          <DatePicker
            id={elementIds.statDateFrom}
            selected={statdatefrom}
            onChange={(date) => setStatdatefrom(date || null)}
            dateFormat="dd/MM/yyyy"
            className="form-control"
            isClearable
            placeholderText="From"
          />
        </Col>
        <Col xs={12} sm={6} lg={4}>
          <DatePicker
            id={elementIds.statDateTo}
            selected={statdateto}
            onChange={(date) => setStatdateto(date || null)}
            dateFormat="dd/MM/yyyy"
            className="form-control"
            isClearable
            placeholderText="To"
          />
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col xs={12} lg={4} className="d-flex align-items-center">Price</Col>
        <Col xs={12} sm={6} lg={4}>
          <Form.Group controlId={elementIds.priceFrom}>
            <Form.Control id={elementIds.priceFrom} value={pricefrom} onChange={(event) => setPricefrom(numericValue(event.target.value))} placeholder="From" />
          </Form.Group>
        </Col>
        <Col xs={12} sm={6} lg={4}>
          <Form.Group controlId={elementIds.priceTo}>
            <Form.Control id={elementIds.priceTo} value={priceto} onChange={(event) => setPriceto(numericValue(event.target.value))} placeholder="To" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col lg={4} />
        <Col>
          {renderAlignedCheckbox(elementIds.priceNull, "Price Missing or $0", pricenull, (event) => setPricenull(event.target.checked))}
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col>
          <Form.Group controlId={elementIds.transferNo}>
            <Form.Control id={elementIds.transferNo} value={transferno} onChange={(event) => setTransferno(event.target.value)} placeholder="Transfer No containing" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col lg={4} />
        <Col>
          {renderAlignedCheckbox(elementIds.transferNoNull, "Transfer No Missing", transfernonull, (event) => setTransfernonull(event.target.checked))}
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col lg={4} />
        <Col>
          {renderAlignedCheckbox(elementIds.transferNoDuplicate, "Duplicate Transfer Numbers", transfernoduplicate, (event) => setTransfernoduplicate(event.target.checked))}
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col xs={12} lg={4} className="d-flex align-items-center">Price Per SQM Bldg</Col>
        <Col xs={12} sm={6} lg={4}>
          <Form.Group controlId={elementIds.pricePerSqmBuildingFrom}>
            <Form.Control id={elementIds.pricePerSqmBuildingFrom} value={pricepsqmbuildingfrom} onChange={(event) => setPricepsqmbuildingfrom(numericValue(event.target.value))} placeholder="From" />
          </Form.Group>
        </Col>
        <Col xs={12} sm={6} lg={4}>
          <Form.Group controlId={elementIds.pricePerSqmBuildingTo}>
            <Form.Control id={elementIds.pricePerSqmBuildingTo} value={pricepsqmbuildingto} onChange={(event) => setPricepsqmbuildingto(numericValue(event.target.value))} placeholder="To" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col xs={12} lg={4} className="d-flex align-items-center">Site Area</Col>
        <Col xs={12} sm={6} lg={4}>
          <Form.Group controlId={elementIds.siteAreaFrom}>
            <Form.Control id={elementIds.siteAreaFrom} value={siteareafrom} onChange={(event) => setSiteareafrom(numericValue(event.target.value))} placeholder="From" />
          </Form.Group>
        </Col>
        <Col xs={12} sm={6} lg={4}>
          <Form.Group controlId={elementIds.siteAreaTo}>
            <Form.Control id={elementIds.siteAreaTo} value={siteareato} onChange={(event) => setSiteareato(numericValue(event.target.value))} placeholder="To" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col xs={12} lg={4} className="d-flex align-items-center">Price Per SQM Land</Col>
        <Col xs={12} sm={6} lg={4}>
          <Form.Group controlId={elementIds.pricePerSqmLandFrom}>
            <Form.Control id={elementIds.pricePerSqmLandFrom} value={pricepsqmlandfrom} onChange={(event) => setPricepsqmlandfrom(numericValue(event.target.value))} placeholder="From" />
          </Form.Group>
        </Col>
        <Col xs={12} sm={6} lg={4}>
          <Form.Group controlId={elementIds.pricePerSqmLandTo}>
            <Form.Control id={elementIds.pricePerSqmLandTo} value={pricepsqmlandto} onChange={(event) => setPricepsqmlandto(numericValue(event.target.value))} placeholder="To" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col xs={12} lg={4} className="d-flex align-items-center">Yield</Col>
        <Col xs={12} sm={6} lg={4}>
          <Form.Group controlId={elementIds.yieldFrom}>
            <Form.Control id={elementIds.yieldFrom} value={yieldamtfrom} onChange={(event) => setYieldamtfrom(numericValue(event.target.value))} placeholder="From" />
          </Form.Group>
        </Col>
        <Col xs={12} sm={6} lg={4}>
          <Form.Group controlId={elementIds.yieldTo}>
            <Form.Control id={elementIds.yieldTo} value={yieldamtto} onChange={(event) => setYieldamtto(numericValue(event.target.value))} placeholder="To" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col>
          <Select
            inputId={elementIds.broadSpaceUse}
            value={selectedBroadSpaceUseCategories}
            onChange={(option) => {
              setSelectedBroadSpaceUseCategories(option || null);
              setBroadspace(option?.label ?? "");
              setBroadspaceValue(option?.value ?? "");
              setSelectedSpaceUseCategories(null);
              setSpaceuse("");
            }}
            options={broadSpaceUseOptions || []}
            placeholder="Broad Use"
            isClearable
          />
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col>
          <Select
            inputId={elementIds.spaceUse}
            value={selectedSpaceUseCategories}
            onChange={(option) => {
              setSelectedSpaceUseCategories(option || null);
              setSpaceuse(option?.label ?? "");
              const broadSpaceValue = option?.supplementary_details?.broad_space_use_id;
              if (broadSpaceValue) {
                setBroadspaceValue(broadSpaceValue);
                const matchingBroadOption = (broadSpaceUseOptions || []).find((broadOption) => String(broadOption?.value) === String(broadSpaceValue));
                setBroadspace(matchingBroadOption?.label ?? option?.supplementary_details?.broad_space_use_code ?? "");
                setSelectedBroadSpaceUseCategories({
                  value: broadSpaceValue,
                  label: matchingBroadOption?.label ?? option?.supplementary_details?.broad_space_use_code ?? String(broadSpaceValue),
                });
              }
            }}
            options={filteredSpaceUseOptions}
            placeholder="Space Use"
            isClearable
          />
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col>
          <Select
            inputId={elementIds.titleBasis}
            value={selectedTitleBasisCategories}
            onChange={(option) => {
              setSelectedTitleBasisCategories(option || null);
              setTitlebasis(option?.label ?? "");
            }}
            options={titleBasisOptions || []}
            placeholder="Title Basis"
            isClearable
          />
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col>
          <Select
            inputId={elementIds.saleMethod}
            value={selectedSaleMethodCategories}
            onChange={(option) => {
              setSelectedSaleMethodCategories(option || null);
              setSalesmethod(option?.label ?? "");
            }}
            options={saleMethodOptions || []}
            placeholder="Sale Method"
            isClearable
          />
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col>
          <Form.Group controlId={elementIds.buildingName}>
            <Form.Control id={elementIds.buildingName} value={buildingname} onChange={(event) => setBuildingname(event.target.value)} placeholder="Building Name containing" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col xs={12} sm={8}>
          <Form.Group controlId={elementIds.streetName}>
            <Form.Control id={elementIds.streetName} value={streetname} onChange={(event) => setStreetname(event.target.value)} placeholder="Street Name starting" />
          </Form.Group>
        </Col>
        <Col xs={12} sm={4}>
          <Form.Group controlId={elementIds.streetExt}>
            <Form.Control id={elementIds.streetExt} value={streetext} onChange={(event) => setStreetext(event.target.value)} placeholder="Street Ext" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col>
          <Form.Group controlId={elementIds.suburb}>
            <Form.Control id={elementIds.suburb} value={suburb} onChange={(event) => setSuburb(event.target.value)} placeholder="Suburb starting" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col xs={12} sm={8}>
          <Select
            inputId={elementIds.state}
            value={selectedState}
            onChange={(option) => {
              setSelectedState(option || null);
              setStateCode(option?.value ?? "");
            }}
            options={stateOptions}
            placeholder="Select a state"
            isClearable
          />
        </Col>
        <Col xs={12} sm={4}>
          <Form.Group controlId={elementIds.postcode}>
            <Form.Control id={elementIds.postcode} value={postcode} onChange={(event) => setPostcode(event.target.value)} placeholder="Postcode" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col>
          <Form.Group controlId={elementIds.agent}>
            <Form.Control id={elementIds.agent} value={agent} onChange={(event) => setAgent(event.target.value)} placeholder="Agent containing" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col>
          <Form.Group controlId={elementIds.agency}>
            <Form.Control id={elementIds.agency} value={agency} onChange={(event) => setAgency(event.target.value)} placeholder="Agency containing" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col>
          <Form.Group controlId={elementIds.buyer}>
            <Form.Control id={elementIds.buyer} value={buyer} onChange={(event) => setBuyer(event.target.value)} placeholder="Buyer containing" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col>
          <Form.Group controlId={elementIds.vendor}>
            <Form.Control id={elementIds.vendor} value={vendor} onChange={(event) => setVendor(event.target.value)} placeholder="Vendor containing" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col xs="auto">
          <Button id={elementIds.exportPimsButton} type="button" size="sm" onClick={exportPimsRecords}>
            Export PIMS
          </Button>
        </Col>
        <Col xs="auto">
          <Button id={elementIds.exportRcaMsciButton} type="button" size="sm" onClick={exportRcaMsciRecords}>
            Export RCA/MSCI
          </Button>
        </Col>
      </Row>

      <Row className="mb-2 g-2">
        <Col xs="auto">
          <Button id={elementIds.reviewBuyerSellerButton} type="button" size="sm" onClick={getIsCompanyNameCheck}>
            Review Individual Buyer/Sellers
          </Button>
        </Col>
      </Row>
    </>
  );

  if (!isAuthenticated) {
    return null;
  }

  return (
    <SLMSearchPageShell
      className="SLMSaleSearch"
      title="SLM Sales"
      isLoading={isLoading}
      onSubmit={handleSubmit}
      onClear={clearFilters}
      showDefaultActions={false}
      topActions={topActions}
      filterPanel={filters}
      filterDensity="tight"
      headerExtras={
        <Modal
          id={elementIds.mappingModal}
          dialogClassName="modal-95w"
          show={showIndividualBuyerSeller}
          onHide={() => setShowIndividualBuyerSeller(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Buyers/Sellers Not Flagged as a Business</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <SLMOwnerIsCompanyMapping
              user={user}
              isAuthenticated={isAuthenticated}
              data={ownerIsCompanyMapping}
              updateTableState={updateOwnerIsCompanyMapping}
            />
          </Modal.Body>
        </Modal>
      }
      resultsPanel={
        <SLMSaleList
          filter={filter}
          isAuthenticated={isAuthenticated}
          isLoading={isLoading}
          data={sales}
          search_count={searchCount}
        />
      }
    />
  );
}

export default SLMSaleSearch;
